import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, RefreshCw, ArrowDownToLine, X,
  CheckCircle2, AlertCircle, Clock, Loader2, Ban,
  ChevronRight, Copy, CheckCheck,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate, debounce } from '../utils/helpers';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  RECEIVED:     { cls: 'bg-blue-100 text-blue-800',   icon: Clock        },
  CBS_NOTIFIED: { cls: 'bg-purple-100 text-purple-800', icon: Loader2    },
  RESPONDED:    { cls: 'bg-green-100 text-green-800',  icon: CheckCircle2 },
  FAILED:       { cls: 'bg-red-100 text-red-800',     icon: AlertCircle  },
  ABORTED:      { cls: 'bg-yellow-100 text-yellow-800', icon: Ban        },
};

const TYPE_STYLE = {
  PARTY_LOOKUP:   { cls: 'bg-cyan-100 text-cyan-800',     label: 'Party Lookup'   },
  QUOTE:          { cls: 'bg-indigo-100 text-indigo-800',  label: 'Quote'          },
  TRANSFER:       { cls: 'bg-emerald-100 text-emerald-800', label: 'Transfer'      },
  TRANSFER_ERROR: { cls: 'bg-orange-100 text-orange-800', label: 'Transfer Error' },
};

const ALL_STATUSES      = ['RECEIVED', 'CBS_NOTIFIED', 'RESPONDED', 'FAILED', 'ABORTED'];
const ALL_REQUEST_TYPES = ['PARTY_LOOKUP', 'QUOTE', 'TRANSFER', 'TRANSFER_ERROR'];

// ── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] ?? { cls: 'bg-gray-100 text-gray-700', icon: Clock };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const t = TYPE_STYLE[type] ?? { cls: 'bg-gray-100 text-gray-700', label: type };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${t.cls}`}>
      {t.label}
    </span>
  );
};

// Timing pill shown in detail drawer
const TimingRow = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-xs font-mono text-gray-700">{value ?? '—'}</span>
  </div>
);

// JSON/text payload viewer with copy button
const PayloadViewer = ({ label, value, accent = 'gray' }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const prettyPrint = (raw) => {
    if (!raw) return null;
    try { return JSON.stringify(JSON.parse(raw), null, 2); }
    catch { return raw; }
  };

  const pretty = prettyPrint(value);

  const ACCENT = {
    gray:   'border-gray-200 bg-gray-50',
    blue:   'border-blue-200 bg-blue-50',
    green:  'border-green-200 bg-green-50',
    red:    'border-red-200 bg-red-50',
    purple: 'border-purple-200 bg-purple-50',
  };

  return (
    <div className={`rounded-lg border ${ACCENT[accent]} overflow-hidden`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-inherit">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
        {value && (
          <button
            onClick={copy}
            className="p-1 rounded hover:bg-white/60 text-gray-400 hover:text-gray-600 transition-colors"
            title="Copy to clipboard"
          >
            {copied
              ? <CheckCheck className="w-3.5 h-3.5 text-green-500" />
              : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {pretty ? (
        <pre className="p-3 text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap break-all max-h-64">
          {pretty}
        </pre>
      ) : (
        <p className="px-3 py-2 text-xs text-gray-400 italic">empty</p>
      )}
    </div>
  );
};

// ── Detail drawer ────────────────────────────────────────────────────────────

const DetailDrawer = ({ record, onClose }) => {
  if (!record) return null;

  const latencyMs = (() => {
    if (!record.receivedAt || !record.respondedAt) return null;
    const diff = new Date(record.respondedAt) - new Date(record.receivedAt);
    return diff >= 0 ? `${diff} ms` : null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex justify-end" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TypeBadge type={record.requestType} />
              <StatusBadge status={record.status} />
            </div>
            <p className="text-sm font-mono text-gray-500 break-all">
              {record.resourceId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-4 shrink-0"
            aria-label="Close detail panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Meta */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Request Info
            </h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 divide-y divide-gray-100">
              <TimingRow label="Request ID"     value={record.requestId} />
              <TimingRow label="FSPIOP-Source"  value={record.fspiopSource} />
              <TimingRow label="FSPIOP-Dest"    value={record.fspiopDest} />
              {record.resourceType && (
                <TimingRow label="Resource Type" value={record.resourceType} />
              )}
            </div>
          </section>

          {/* Error */}
          {(record.errorCode || record.errorDetail) && (
            <section>
              <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">
                Error
              </h3>
              <div className="bg-red-50 rounded-lg border border-red-200 px-3 divide-y divide-red-100">
                {record.errorCode   && <TimingRow label="Error Code"   value={record.errorCode} />}
                {record.errorDetail && <TimingRow label="Error Detail" value={record.errorDetail} />}
              </div>
            </section>
          )}

          {/* Timing */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Timing
            </h3>
            <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 divide-y divide-gray-100">
              <TimingRow label="Received at"     value={formatDate(record.receivedAt,    'PPpp')} />
              <TimingRow label="CBS notified at" value={formatDate(record.cbsNotifiedAt, 'PPpp')} />
              <TimingRow label="Responded at"    value={formatDate(record.respondedAt,   'PPpp')} />
              {latencyMs && <TimingRow label="End-to-end latency" value={latencyMs} />}
            </div>
          </section>

          {/* Payloads */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Payloads
            </h3>
            <PayloadViewer label="→ Hub Request (received)"   value={record.requestPayload} accent="blue" />
            <PayloadViewer label="→ CBS Request (sent)"       value={record.cbsRequest}     accent="purple" />
            <PayloadViewer label="← CBS Response (received)"  value={record.cbsResponse}    accent="green" />
            <PayloadViewer label="→ Hub Callback (sent back)" value={record.hubResponse}     accent="gray" />
          </section>
        </div>
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const PayeeRequests = () => {
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ status: '', requestType: '', search: '' });
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected]     = useState(null);   // record shown in drawer

  const load = useCallback(async (pg = page, sz = pageSize) => {
    try {
      setLoading(true);
      const data = await mojaloopApi.getPayeeRequests({
        status:      filters.status,
        requestType: filters.requestType,
        search:      filters.search,
        page: pg,
        size: sz,
      });
      setRecords(data.content    ?? []);
      setTotalItems(data.totalItems ?? 0);
      setTotalPages(data.totalPages ?? 0);
      setPage(data.page ?? 0);
    } catch (err) {
      console.error('[PayeeRequests] load error:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]); // eslint-disable-line

  useEffect(() => { setPage(0); load(0, pageSize); }, [filters]); // eslint-disable-line
  useEffect(() => { load(page, pageSize); }, [page, pageSize]);   // eslint-disable-line

  const handleSearchChange  = debounce((v) => setFilters(f => ({ ...f, search: v })), 300);
  const handlePageChange    = (p) => setPage(p - 1);
  const handlePageSizeChange = (s) => { setPageSize(s); setPage(0); };
  const clearFilters         = () => setFilters({ status: '', requestType: '', search: '' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payee Requests</h1>
          <p className="text-gray-600 mt-1">
            Inbound Hub-initiated requests — this DFSP acting as payee FSP
          </p>
        </div>
        <button
          onClick={() => load(page, pageSize)}
          disabled={loading}
          className="btn-primary flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Resource ID, payer FSP, request ID…"
                  className="input pl-10"
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>

            {/* Request type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Request Type</label>
              <select
                className="select"
                value={filters.requestType}
                onChange={(e) => setFilters(f => ({ ...f, requestType: e.target.value }))}
              >
                <option value="">All Types</option>
                {ALL_REQUEST_TYPES.map(t => (
                  <option key={t} value={t}>{TYPE_STYLE[t]?.label ?? t}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                {ALL_STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Results{totalItems > 0 ? ` (${totalItems.toLocaleString()})` : ''}
          </h2>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading payee requests…</p>
              </div>
            </div>
          ) : records.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <ArrowDownToLine className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No payee requests found</p>
                <p className="text-gray-600 text-sm">
                  Payee requests are created when the Mojaloop Hub sends party lookup,
                  quote, or transfer requests to this DFSP.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['#', 'Type', 'Resource ID', 'Payer FSP', 'Payee FSP', 'Status', 'Received', ''].map(h => (
                        <th
                          key={h}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {records.map((r) => (
                      <tr
                        key={r.id}
                        className={`transition-colors cursor-pointer ${
                          selected?.id === r.id
                            ? 'bg-primary-50'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setSelected(r)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-400">
                          #{r.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <TypeBadge type={r.requestType} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 max-w-xs">
                          <span title={r.resourceId}>
                            {r.resourceId?.length > 20
                              ? `${r.resourceId.substring(0, 20)}…`
                              : r.resourceId}
                          </span>
                          {r.resourceType && (
                            <span className="ml-1.5 text-xs text-gray-400">({r.resourceType})</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {r.fspiopSource ?? <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {r.fspiopDest ?? <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={r.status} />
                          {r.errorCode && (
                            <span className="ml-1.5 text-xs font-mono text-red-500">
                              {r.errorCode}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(r.receivedAt, 'PPp')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={page + 1}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer record={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default PayeeRequests;
